import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../util/firebase';
import { sortUsersByPremium } from '../../util/userUtils';
import { usePremium } from '../../util/PremiumFilter';
import { isNativeApp } from '../../util/platformDetection';

const SearchMobile = ({ user }) => {
  const history = useHistory();
  const { renderUserWithBadge, PREMIUM_TIERS } = usePremium();
  const searchInputRef = useRef(null);

  // Default user handles to query
  const defaultUserHandles = ['Christopher', 'charlize', 'thechessnerd', 'Wentan', 'Marcus'];

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState({
    users: [],
    followedUsers: []
  });
  const [defaultUsers, setDefaultUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialSearchLoaded, setInitialSearchLoaded] = useState(false);

  // Fetch default users function
  const fetchDefaultUsers = async () => {
    try {
      const defaultUsersData = [];
      
      // Query each default user handle
      for (const handle of defaultUserHandles) {
        try {
          const usersQuery = query(
            collection(db, "users"),
            where("handle", "==", handle)
          );
          
          const userQuerySnap = await getDocs(usersQuery);
          
          if (!userQuerySnap.empty) {
            const userData = userQuerySnap.docs[0].data();
            defaultUsersData.push({
              id: userQuerySnap.docs[0].id,
              name: userData.handle,
              isPremium: userData.isPremium || false,
              premium: userData.premium !== undefined ? userData.premium : (userData.isPremium ? 1 : 0),
              photoUrl: userData.photoUrl && userData.photoUrl.trim() ? userData.photoUrl : null,
              imageUrl: userData.imageUrl && userData.imageUrl.trim() ? userData.imageUrl : null, // Keep both for compatibility
              isFollowed: false // We'll assume these aren't followed for now
            });
          }
        } catch (e) {
          console.error("Error fetching default user:", handle, e);
        }
      }
      
      // Sort by premium status
      const sortedDefaultUsers = sortUsersByPremium(defaultUsersData);
      setDefaultUsers(sortedDefaultUsers);
      
    } catch (error) {
      console.error("Error fetching default users:", error);
    }
  };

  // Fetch followed users function
  const fetchFollowedUsers = async () => {
    try {
      setIsLoading(true);

      // Get authenticated user handle from Redux state
      const currentUserHandle = user?.credentials?.handle;

      if (!currentUserHandle) {
        setIsLoading(false);
        return [];
      }

      // Reference to the current user's document
      const userRef = doc(db, "users", currentUserHandle);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        setIsLoading(false);
        return [];
      }

      const userData = userSnap.data();
      const followingList = userData.following || [];

      if (followingList.length === 0) {
        setIsLoading(false);
        return [];
      }

      // Get details for each followed user
      const followedUsers = [];

      try {
        // Create a batch query to get all followed users at once
        if (followingList.length <= 10) {
          const usersRef = collection(db, "users");
          const q = query(usersRef, where("handle", "in", followingList));
          const querySnapshot = await getDocs(q);

          querySnapshot.forEach((doc) => {
            const userData = doc.data();
            followedUsers.push({
              id: doc.id,
              name: userData.handle,
              isPremium: userData.isPremium || false,
              premium: userData.premium !== undefined ? userData.premium : (userData.isPremium ? 1 : 0),
              photoUrl: userData.photoUrl || null,
              imageUrl: userData.imageUrl || null, // Keep both for compatibility
              isFollowed: true
            });
          });
        } else {
          // Handle more than 10 following users by chunking the requests
          for (let i = 0; i < followingList.length; i += 10) {
            const chunk = followingList.slice(i, i + 10);
            const usersRef = collection(db, "users");
            const q = query(usersRef, where("handle", "in", chunk));
            const querySnapshot = await getDocs(q);

            querySnapshot.forEach((doc) => {
              const userData = doc.data();
              followedUsers.push({
                id: doc.id,
                name: userData.handle,
                isPremium: userData.isPremium || false,
                premium: userData.premium !== undefined ? userData.premium : (userData.isPremium ? 1 : 0),
                photoUrl: userData.photoUrl || null,
                imageUrl: userData.imageUrl || null, // Keep both for compatibility
                isFollowed: true
              });
            });
          }
        }
      } catch (error) {
        console.error("Error in batch query:", error);
      }

      setIsLoading(false);
      return followedUsers;
    } catch (error) {
      console.error("Error fetching followed users:", error);
      setIsLoading(false);
      return [];
    }
  };

  // Search function
  const handleSearch = async (term) => {
    if (!term.trim()) {
      // Load initial data when search term is empty
      if (!initialSearchLoaded) {
        const followedUsers = await fetchFollowedUsers();
        const sortedFollowedUsers = sortUsersByPremium(followedUsers);

        setSearchResults({
          users: [],
          followedUsers: sortedFollowedUsers.slice(0, 10)
        });

        setInitialSearchLoaded(true);
      } else {
        setSearchResults(prevResults => ({
          ...prevResults,
          users: []
        }));
      }
      return;
    }

    setIsLoading(true);

    try {
      // Use existing followed users if we already loaded them
      let followedUsers = searchResults.followedUsers;

      if (followedUsers.length === 0 && !initialSearchLoaded) {
        followedUsers = await fetchFollowedUsers();
        setInitialSearchLoaded(true);
      }

      const followedHandles = followedUsers.map(user => user.name.toLowerCase());

      // Query for users where handle contains the search term
      const userSnapshot = await getDocs(collection(db, "users"));
      const userResults = [];

      userSnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.handle &&
          userData.handle.toLowerCase().includes(term.toLowerCase())) {

          const isFollowed = followedHandles.includes(userData.handle.toLowerCase());

          if (isFollowed) {
            return;
          }

          userResults.push({
            id: doc.id,
            name: userData.handle,
            isPremium: userData.isPremium || false,
            premium: userData.premium !== undefined ? userData.premium : (userData.isPremium ? 1 : 0),
            photoUrl: userData.photoUrl || null,
            imageUrl: userData.imageUrl || null, // Keep both for compatibility
            isFollowed: false
          });
        }
      });

      // Filter followed users by search term
      const filteredFollowedUsers = followedUsers.filter(user =>
        user.name.toLowerCase().includes(term.toLowerCase())
      ).slice(0, 10);

      const maxNonFollowedUsers = Math.max(0, 10 - filteredFollowedUsers.length);
      const limitedUserResults = userResults.slice(0, maxNonFollowedUsers);
      const sortedUserResults = sortUsersByPremium(limitedUserResults);

      setSearchResults({
        users: sortedUserResults,
        followedUsers: sortUsersByPremium(filteredFollowedUsers)
      });
    } catch (error) {
      console.error("Error searching:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Load initial data on mount
  useEffect(() => {
    handleSearch('');
    fetchDefaultUsers(); // Fetch the real default users data
  }, []);

  // Helper function to render username with badge from search results
  const renderSearchResultUser = (user) => {
    // If user is a simple string, just render with username
    if (typeof user === 'string') {
      return renderUserWithBadge(user);
    }
    
    // If user is an object, extract name and convert isPremium to premium tier
    if (user && typeof user === 'object') {
      const username = user.name || user.handle || '';
      
      // Convert isPremium boolean to premium number (0-3)
      // If user has premium field directly, use it
      // Otherwise, convert isPremium boolean to BRONZE tier (1)
      let premiumTier = PREMIUM_TIERS.NONE;
      if (user.premium !== undefined) {
        premiumTier = user.premium; // Use existing premium value if present
      } else if (user.isPremium === true) {
        premiumTier = PREMIUM_TIERS.BRONZE; // Convert boolean to BRONZE tier
      }
      
      // Ensure empty strings are converted to null to trigger fallback
      const imageUrl = user.photoUrl || user.imageUrl;
      const finalImageUrl = imageUrl && imageUrl.trim() ? imageUrl : null;
      
      return renderUserWithBadge(username, premiumTier, true, finalImageUrl);
    }
    
    // Fallback
    return null;
  };

  const handleResultClick = (result, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Navigate to user profile with back navigation context
    history.push(`/profile/${result.name}`, {
      returnTo: '/search',
      returnLabel: 'Back to Search',
      from: 'search'
    });
  };

  return (
    <div style={{
      width: '100vw',
      height: 'calc(100dvh - 55px)', // Account for bottom navigation
      backgroundColor: '#1a1a1a',
      display: 'flex',
      flexDirection: 'column',
      paddingTop: 'env(safe-area-inset-top)', // iPhone notch safe area
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#1a1a1a',
        borderBottom: '1px solid #333',
        padding: '15px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <h2 style={{
          color: 'gold',
          margin: 0,
          fontSize: '24px',
          fontWeight: 'bold'
        }}>
          Search
        </h2>
        <button
          onClick={() => history.goBack()}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: '#888',
            fontSize: '28px',
            cursor: 'pointer',
            padding: '5px'
          }}
        >
          ✕
        </button>
      </div>

      {/* Search Input */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid #333',
        flexShrink: 0
      }}>
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center'
        }}>
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search for users..."
            style={{
              width: '100%',
              padding: '15px 50px 15px 20px',
              border: '2px solid #333',
              borderRadius: '10px',
              backgroundColor: '#2a2a2a',
              color: 'white',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s ease'
            }}
            onFocus={(e) => e.target.style.borderColor = 'gold'}
            onBlur={(e) => e.target.style.borderColor = '#333'}
          />
          <i
            className="fa-solid fa-magnifying-glass"
            style={{
              position: 'absolute',
              right: '20px',
              color: '#666',
              fontSize: '18px',
              pointerEvents: 'none'
            }}
          />
        </div>
      </div>


      {/* Search Results */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: 'env(safe-area-inset-bottom)' // iPhone gesture area
      }}>
        {isLoading ? (
          <div style={{ 
            padding: '40px 20px', 
            color: '#666', 
            textAlign: 'center',
            fontSize: '16px'
          }}>
            <i className="fa fa-spinner fa-spin" style={{ marginRight: '10px' }}></i>
            Loading...
          </div>
        ) : (
          <>
            {/* Followed Users section */}
            {searchResults.followedUsers && searchResults.followedUsers.length > 0 && (
              <div>
                {searchResults.followedUsers.map(user => (
                  <div
                    key={user.id}
                    style={{
                      padding: '15px 20px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #333',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'background-color 0.2s ease'
                    }}
                    onClick={(e) => handleResultClick(user, e)}
                    onTouchStart={(e) => e.target.style.backgroundColor = '#2a2a2a'}
                    onTouchEnd={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    {renderSearchResultUser(user)}
                  </div>
                ))}
              </div>
            )}

            {/* Other Users section */}
            {searchResults.users && searchResults.users.length > 0 && (
              <div>
                {searchResults.users.map(user => (
                  <div
                    key={user.id}
                    style={{
                      padding: '15px 20px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #333',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'background-color 0.2s ease'
                    }}
                    onClick={(e) => handleResultClick(user, e)}
                    onTouchStart={(e) => e.target.style.backgroundColor = '#2a2a2a'}
                    onTouchEnd={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    {renderSearchResultUser(user)}
                  </div>
                ))}
              </div>
            )}

            {/* No results message */}
            {searchTerm.trim() !== "" &&
              searchResults.users.length === 0 &&
              searchResults.followedUsers.length === 0 && (
                <div style={{ 
                  padding: '40px 20px', 
                  color: '#666', 
                  textAlign: 'center',
                  fontSize: '16px'
                }}>
                  No users found matching "{searchTerm}"
                </div>
              )}

            {/* Show default users when there are no followed users and no search term */}
            {searchTerm.trim() === "" &&
              searchResults.followedUsers.length === 0 && (
                <div>
                  {defaultUsers.map(user => (
                    <div
                      key={user.id}
                      style={{
                        padding: '15px 20px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #333',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'background-color 0.2s ease'
                      }}
                      onClick={(e) => handleResultClick(user, e)}
                      onTouchStart={(e) => e.target.style.backgroundColor = '#2a2a2a'}
                      onTouchEnd={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      {renderSearchResultUser(user)}
                    </div>
                  ))}
                </div>
              )}
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user
});

export default connect(mapStateToProps)(SearchMobile);